import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { graphql } from '~/apollo/generated/v4';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { SOFormFields } from '~/components/upload/supporting-object/so-form-fields';
import type { SOParent } from '~/components/upload/supporting-object/so-manager';
import { useZodForm } from '~/utils/forms';
import type { RefetchQueries } from '~/utils/graphql';
import {
  initialVariogram,
  variogramSchema,
} from '~/utils/modules/supporting-object';

const CREATE_VARIOGRAM = graphql(`
  mutation CreateVariogram($input: VariogramCreateInput!) {
    variogramCreate(input: $input) {
      result {
        id
      }
    }
  }
`);

type Props = SOParent & {
  refetchQueries?: RefetchQueries;
};

export function VariogramCreateForm({
  outcropId,
  studyId,
  refetchQueries,
}: Props) {
  const [createVariogram, { loading, error }] = useMutation(CREATE_VARIOGRAM, {
    refetchQueries,
  });

  const form = useZodForm({
    schema: variogramSchema,
    values: initialVariogram(),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await createVariogram({
        variables: {
          input: {
            ...values,
            outcropId,
            studyId,
          },
        },
      });
      toast.success('Variogram created successfully.');
      form.reset();
    } catch (err) {
      console.log('Error creating variogram', err);
      toast.error('There was a problem creating the variogram.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <SOFormFields soType="VARIOGRAM" studyId={studyId} />
        <HookFormErrors graphQLError={error} />

        <div className="card-actions justify-end">
          <Button type="submit" loading={loading} color="primary">
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
